import { ComponentCO2, ComponentCO2Link } from '@acies/domain';
import { ObservableCollection } from '@acies/observables';
import { FileDropPickerEvent } from '@acies/ui';
import InternalServerError from '@shared/errors/internal-server-error';
import NetworkError from '@shared/errors/network-error';
import UnknownError from '@shared/errors/unknown-error';
import { z } from 'zod';
import CO2Model from '../models/co2-model';
import ImportCSVModel from '../models/import-csv-model';
import CO2Service from '../services/co2-service';

const CSVSchema = z.array(
	z
		.string()
		.min(1)
		.max(55)
		.regex(/^[a-z0-9-]+$/i),
);

export default class ImportCSVController {
	public dialog: HTMLDialogElement | null;
	public set selectedComponentCO2(value: ComponentCO2 | null) {
		if (this.#selectedComponentCO2 === value) {
			return;
		}
		this.#selectedComponentCO2 = value;
		this.#selectedComponentCO2Changed();
	}

	public get selectedComponentCO2(): ComponentCO2 | null {
		return this.#selectedComponentCO2;
	}

	#co2Model: CO2Model;
	#importCSVModel: ImportCSVModel;
	#co2Service: CO2Service;
	#selectedComponentCO2: ComponentCO2 | null;
	#co2LinkCollection: ObservableCollection<ComponentCO2Link>;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.#co2Model = co2Model;
		this.#selectedComponentCO2 = null;
		this.#importCSVModel = co2Model.importCSVModel;
		this.#co2LinkCollection = co2Model.listComponentCO2LinkModel.co2LinkCollection;
		this.#co2LinkCollection.subscribe(this.#co2LinkCollectionChanged.bind(this));
		this.#co2Service = co2Service;
	}

	public dialogImportButtonClicked(): void {
		this.#hideErrorLabels();
		this.#disableCancelButton();
		this.#disableImportButton();
		this.#showSpinner();
		this.#importLinks();
	}

	public downloadCSVButtonClicked(): void {
		const file = this.#getDownloadFileFromComponentCO2Links();
		const link = document.createElement('a');
		const url = URL.createObjectURL(file);
		link.href = url;
		link.download = file.name;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	public fileSelected(event: Event): void {
		if (event instanceof FileDropPickerEvent) {
			void this.#parseCSVFile(event.file);
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
		}
	}

	#getDownloadFileFromComponentCO2Links(): File {
		const rows = this.#co2LinkCollection.collection.map((value: ComponentCO2Link) => value.name);
		const content = rows.join('\r\n');
		return new File([content], `${this.#selectedComponentCO2.name}.csv`, {
			type: 'text/csv;charset=utf-8',
		});
	}

	#co2LinkCollectionChanged(): void {
		this.#importCSVModel.downloadCSVButtonEnabled = this.#co2LinkCollection.length > 0;
	}

	#importLinks(): void {
		this.#co2Service
			.batchCreateComponentCO2Links({
				description: this.#selectedComponentCO2.description,
				epd: this.#selectedComponentCO2.epd,
				links: this.#importCSVModel.importItems,
				name: this.#selectedComponentCO2.name,
				phases: this.#selectedComponentCO2.phases,
			})
			.subscribe((response) => {
				this.#hideSpinner();
				// if we get an Array back, everything went well and we can reload the componentCOLinks
				if (Array.isArray(response)) {
					this.#loadComponentCO2Links();
				} else {
					this.#showImportErrorMessage(response);
				}
			});
	}

	#showImportErrorMessage(error: NetworkError | InternalServerError | UnknownError): void {
		this.#enableCancelButton();
		this.#enableImportButton();
		if (error instanceof NetworkError) {
			this.#showNetworkError();
		} else {
			this.#showUnknownError();
		}
	}

	#loadComponentCO2Links(): void {
		this.#co2Service.listComponentCO2Link(this.#selectedComponentCO2).subscribe((response) => {
			this.closeDialog();
			this.#resetUIFlags();
			if (Array.isArray(response)) {
				this.#importCSVModel.importItems = [];
				this.#co2LinkCollection.clear();
				this.#co2LinkCollection.addItems(response);
			} else {
				this.#co2Model.showGlobalErrorDialog(response);
			}
		});
	}

	#hideImportExportUI(): void {
		this.#importCSVModel.fileDropPickerVisible = false;
		this.#importCSVModel.exportButtonVisible = false;
	}

	#showImportExportUI(): void {
		this.#importCSVModel.fileDropPickerVisible = true;
		this.#importCSVModel.exportButtonVisible = true;
	}

	#selectedComponentCO2Changed(): void {
		if (this.#selectedComponentCO2 instanceof ComponentCO2) {
			this.#showImportExportUI();
		} else {
			this.#hideImportExportUI();
		}
	}

	#showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	async #parseCSVFile(file: File): Promise<void> {
		this.#resetUIFlags();
		try {
			const content = await file.text();
			const rows: string[] = content
				.split(/\r?\n/) // Split by newline (handles Windows and Unix)
				.filter((row) => row.trim() !== ''); // Remove empty lines
			this.#validateCSV(rows);
		} catch (_: unknown) {
			// if any errors are thrown, show invalid error
			// eslint-disable-next-line unused-imports/no-unused-vars
			this.#showInvalidFileError();
			this.#showDialog();
		}
	}

	#validateCSV(value: string[]): void {
		// validate the whole Array of CSVPair
		const safe = CSVSchema.safeParse(value);
		if (safe.success) {
			this.#validateCSVSuccess(safe.data);
		} else {
			// if the schema validation fails, show the error message
			this.#showInvalidFileError();
		}
		this.#showDialog();
	}

	#validateCSVSuccess(data: string[]): void {
		// creating a set removes duplicate items
		const set: Set<string> = new Set<string>(data);
		const unique: string[] = Array.from(set);
		// show the error message if the unique Array is empty
		if (unique.length === 0) {
			this.#showInvalidFileError();
		} else {
			// everything is valid so we can safely set the
			// importItems property and show the populated list
			this.#importCSVModel.importItems = unique;
			this.#showImportList();
			this.#enableImportButton();
		}
	}

	#enableImportButton(): void {
		this.#importCSVModel.importButtonEnabled = true;
	}

	#disableImportButton(): void {
		this.#importCSVModel.importButtonEnabled = false;
	}

	#disableCancelButton(): void {
		this.#importCSVModel.cancelButtonEnabled = false;
	}

	#enableCancelButton(): void {
		this.#importCSVModel.cancelButtonEnabled = true;
	}

	#resetUIFlags(): void {
		this.#hideErrorLabels();
		this.#hideImportList();
		this.#disableImportButton();
		this.#enableCancelButton();
		this.#hideSpinner();
	}

	#hideErrorLabels(): void {
		this.#hideInvalidFileError();
		this.#hideNetworkError();
		this.#hideUnknownError();
	}

	#showInvalidFileError(): void {
		this.#importCSVModel.invalidFileErrrorMessageVisible = true;
	}

	#showNetworkError(): void {
		this.#importCSVModel.networkErrrorMessageVisible = true;
	}

	#hideNetworkError(): void {
		this.#importCSVModel.networkErrrorMessageVisible = false;
	}

	#showUnknownError(): void {
		this.#importCSVModel.unknownErrrorMessageVisible = true;
	}

	#hideUnknownError(): void {
		this.#importCSVModel.unknownErrrorMessageVisible = false;
	}

	#showImportList(): void {
		this.#importCSVModel.importListVisible = true;
	}

	#hideImportList(): void {
		this.#importCSVModel.importListVisible = false;
	}

	#hideInvalidFileError(): void {
		this.#importCSVModel.invalidFileErrrorMessageVisible = false;
	}

	#showSpinner(): void {
		this.#importCSVModel.spinnerVisible = true;
	}

	#hideSpinner(): void {
		this.#importCSVModel.spinnerVisible = false;
	}
}
